import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import FloatingNotification, { FloatingNotificationData } from './FloatingNotification';
import { EDGE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from '../services/supabaseClient';
import { useDashboard } from '../context/DashboardContext';

interface FloatingNotificationsContainerProps {
  enterpriseId: string | null;
  userId?: string;
}

const FloatingNotificationsContainer: React.FC<FloatingNotificationsContainerProps> = ({
  enterpriseId,
  userId,
}) => {
  const { language } = useDashboard();
  const [notifications, setNotifications] = useState<FloatingNotificationData[]>([]);

  // Subscribe to new Emily messages
  useEffect(() => {
    if (!enterpriseId) return;

    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let ch: ReturnType<typeof supabase.channel> | null = null;

    const startSubscription = () => {
      ch = supabase
        .channel(`emily-messages-${enterpriseId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'api',
            table: 'emily_messages',
          },
          async (payload: any) => {
            const msg = payload.new;
            if (msg.role !== 'candidate') return;

            // Resolve candidate name from candidate_id
            let candidateName = 'Candidat';
            const candidateId = msg.candidate_id || '';
            if (candidateId) {
              const { data: cand } = await supabase
                .from('candidates').select('candidate_name').eq('id', candidateId).maybeSingle();
              if (cand?.candidate_name) candidateName = cand.candidate_name;
            }

            const newNotif: FloatingNotificationData = {
              id: msg.id,
              candidateId,
              candidateName,
              message: msg.content || msg.message_text || '[Sans texte]',
              timestamp: new Date(msg.created_at),
              type: 'emily_message',
            };
            setNotifications(prev => [...prev, newNotif]);
            setTimeout(() => {
              setNotifications(prev => prev.filter(n => n.id !== msg.id));
            }, 8000);
          },
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            supabase.removeChannel(ch!);
            retryTimer = setTimeout(startSubscription, 5000);
          }
        });
    };

    startSubscription();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (ch) supabase.removeChannel(ch);
    };
  }, [enterpriseId]);

  const handleReply = async (notificationId: string, replyText: string) => {
    const notif = notifications.find(n => n.id === notificationId);
    if (!notif) return;

    try {
      const response = await fetch(`${EDGE_FUNCTIONS_URL}/functions/v1/emily-reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: 'recruiter_msg',
          token: notif.candidateId,
          message: replyText,
        }),
      });

      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
      }
    } catch (err) {
      console.error('Failed to send reply:', err);
    }
  };

  return (
    <div>
      {notifications.map(notif => (
        <FloatingNotification
          key={notif.id}
          notification={notif}
          language={language}
          onClose={() => {
            setNotifications(prev => prev.filter(n => n.id !== notif.id));
          }}
          onReply={replyText => handleReply(notif.id, replyText)}
        />
      ))}
    </div>
  );
};

export default FloatingNotificationsContainer;
